// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/user/components/profile/AvatarDecorationOverlay.module.css';
import React from 'react';

interface AvatarDecorationOverlayProps {
	decoration?: string | null;
	size?: number;
	className?: string;
}

export const AvatarDecorationOverlay: React.FC<AvatarDecorationOverlayProps> = ({
	decoration,
	className,
}) => {
	if (!decoration || decoration === 'none') return null;

	return (
		<div className={`${styles.overlay} ${className || ''}`.trim()} aria-hidden="true">
			<svg
				viewBox="0 0 100 100"
				className={styles.svgRoot}
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				{renderDecorationContent(decoration)}
			</svg>
		</div>
	);
};

function renderDecorationContent(id: string): React.ReactNode {
	switch (id) {
		case 'cat_ears':
			return (
				<g id="dec-cat-ears">
					<defs>
						<linearGradient id="catEarGrad" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="#ff7eb6" />
							<stop offset="100%" stopColor="#ff4d94" />
						</linearGradient>
						<linearGradient id="catInnerGrad" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="#fff0f5" />
							<stop offset="100%" stopColor="#ffb3d1" />
						</linearGradient>
					</defs>
					{/* Left Ear */}
					<path
						d="M 23 29 C 19 14, 26 5, 38 17 C 33 23, 29 26, 23 29 Z"
						fill="url(#catEarGrad)"
						stroke="#d6336c"
						strokeWidth="1.2"
						strokeLinejoin="round"
					/>
					<path
						d="M 26 26 C 24 16, 29 11, 35 18 C 32 22, 29 24, 26 26 Z"
						fill="url(#catInnerGrad)"
					/>
					{/* Right Ear */}
					<path
						d="M 77 29 C 81 14, 74 5, 62 17 C 67 23, 71 26, 77 29 Z"
						fill="url(#catEarGrad)"
						stroke="#d6336c"
						strokeWidth="1.2"
						strokeLinejoin="round"
					/>
					<path
						d="M 74 26 C 76 16, 71 11, 65 18 C 68 22, 71 24, 74 26 Z"
						fill="url(#catInnerGrad)"
					/>
					{/* Left Whiskers */}
					<line x1="7" y1="48" x2="16" y2="50" stroke="#ff7eb6" strokeWidth="1.4" strokeLinecap="round" />
					<line x1="8" y1="55" x2="16" y2="54" stroke="#ff7eb6" strokeWidth="1.4" strokeLinecap="round" />
					{/* Right Whiskers */}
					<line x1="93" y1="48" x2="84" y2="50" stroke="#ff7eb6" strokeWidth="1.4" strokeLinecap="round" />
					<line x1="92" y1="55" x2="84" y2="54" stroke="#ff7eb6" strokeWidth="1.4" strokeLinecap="round" />
					{/* Left Paw Accent */}
					<g transform="translate(18, 76)">
						<circle cx="0" cy="0" r="4.5" fill="#fff0f5" stroke="#ff7eb6" strokeWidth="1.2" />
						<circle cx="-3" cy="-4" r="1.5" fill="#ff7eb6" />
						<circle cx="0" cy="-5" r="1.6" fill="#ff7eb6" />
						<circle cx="3" cy="-4" r="1.5" fill="#ff7eb6" />
					</g>
				</g>
			);

		case 'sakura':
			return (
				<g id="dec-sakura">
					<defs>
						<linearGradient id="sakuraPink" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#fff0f6" />
							<stop offset="100%" stopColor="#ff85c0" />
						</linearGradient>
					</defs>
					{/* Flowing vine branch */}
					<path
						d="M 16 36 C 14 22, 28 12, 42 13 C 58 14, 76 12, 84 24"
						stroke="#d48806"
						strokeWidth="1.5"
						strokeLinecap="round"
						fill="none"
					/>
					{/* Flower Top Left */}
					<g transform="translate(26, 18)" className={styles.animatedPetal}>
						<circle cx="0" cy="-4" r="3" fill="url(#sakuraPink)" />
						<circle cx="4" cy="-1" r="3" fill="url(#sakuraPink)" />
						<circle cx="2" cy="4" r="3" fill="url(#sakuraPink)" />
						<circle cx="-3" cy="3" r="3" fill="url(#sakuraPink)" />
						<circle cx="-4" cy="-2" r="3" fill="url(#sakuraPink)" />
						<circle cx="0" cy="0" r="1.8" fill="#ffd666" />
					</g>
					{/* Flower Top Right */}
					<g transform="translate(76, 20)" className={styles.animatedPetal}>
						<circle cx="0" cy="-4" r="3" fill="url(#sakuraPink)" />
						<circle cx="4" cy="-1" r="3" fill="url(#sakuraPink)" />
						<circle cx="2" cy="4" r="3" fill="url(#sakuraPink)" />
						<circle cx="-3" cy="3" r="3" fill="url(#sakuraPink)" />
						<circle cx="-4" cy="-2" r="3" fill="url(#sakuraPink)" />
						<circle cx="0" cy="0" r="1.8" fill="#ffd666" />
					</g>
					{/* Floating Petals */}
					<path d="M 12 46 C 9 44, 10 39, 14 41 C 18 43, 16 48, 12 46 Z" fill="#ffadd2" className={styles.animatedPetal} />
					<path d="M 88 52 C 85 50, 86 45, 90 47 C 94 49, 92 54, 88 52 Z" fill="#ffadd2" className={styles.animatedPetal} />
					<path d="M 32 86 C 29 84, 30 79, 34 81 C 38 83, 36 88, 32 86 Z" fill="#ffadd2" className={styles.animatedPetal} />
					<path d="M 68 85 C 65 83, 66 78, 70 80 C 74 82, 72 87, 68 85 Z" fill="#ffadd2" className={styles.animatedPetal} />
				</g>
			);

		case 'cyberpunk_neon':
			return (
				<g id="dec-cyberpunk" className={styles.animatedCyber}>
					<defs>
						<linearGradient id="cyberCyan" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#00f0ff" />
							<stop offset="100%" stopColor="#0077ff" />
						</linearGradient>
						<linearGradient id="cyberPink" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#ff007f" />
							<stop offset="100%" stopColor="#b000ff" />
						</linearGradient>
					</defs>
					{/* Outer dashed HUD ring */}
					<circle
						cx="50"
						cy="50"
						r="43"
						stroke="url(#cyberCyan)"
						strokeWidth="1.5"
						strokeDasharray="14 8 4 8"
						fill="none"
					/>
					<circle
						cx="50"
						cy="50"
						r="46"
						stroke="url(#cyberPink)"
						strokeWidth="0.8"
						strokeDasharray="4 20"
						fill="none"
					/>
					{/* Top Left Bracket */}
					<path d="M 12 24 L 12 12 L 24 12" stroke="#00f0ff" strokeWidth="2.4" strokeLinecap="round" fill="none" />
					<rect x="15" y="15" width="2" height="2" fill="#ff007f" />
					{/* Top Right Bracket */}
					<path d="M 88 24 L 88 12 L 76 12" stroke="#00f0ff" strokeWidth="2.4" strokeLinecap="round" fill="none" />
					<rect x="83" y="15" width="2" height="2" fill="#ff007f" />
					{/* Bottom Left Bracket */}
					<path d="M 12 76 L 12 88 L 24 88" stroke="#ff007f" strokeWidth="2.4" strokeLinecap="round" fill="none" />
					<rect x="15" y="83" width="2" height="2" fill="#00f0ff" />
					{/* Bottom Right Bracket */}
					<path d="M 88 76 L 88 88 L 76 88" stroke="#ff007f" strokeWidth="2.4" strokeLinecap="round" fill="none" />
					<rect x="83" y="83" width="2" height="2" fill="#00f0ff" />
					{/* HUD Status Text / Ticks */}
					<line x1="50" y1="6" x2="50" y2="9" stroke="#00f0ff" strokeWidth="2" strokeLinecap="round" />
					<line x1="50" y1="91" x2="50" y2="94" stroke="#ff007f" strokeWidth="2" strokeLinecap="round" />
				</g>
			);

		case 'fire_aura':
			return (
				<g id="dec-fire" className={styles.animatedFlame}>
					<defs>
						<linearGradient id="fireBase" x1="0" y1="1" x2="0" y2="0">
							<stop offset="0%" stopColor="#e03131" />
							<stop offset="50%" stopColor="#ff922b" />
							<stop offset="100%" stopColor="#ffd43b" />
						</linearGradient>
						<linearGradient id="fireTip" x1="0" y1="1" x2="0" y2="0">
							<stop offset="0%" stopColor="#ff6b6b" />
							<stop offset="100%" stopColor="#fff9db" />
						</linearGradient>
					</defs>
					{/* Flame crowns on sides & bottom */}
					<path
						d="M 16 68 C 10 54, 11 40, 16 32 C 18 42, 21 44, 21 48 C 21 34, 27 22, 34 16 C 33 26, 36 29, 39 30 C 43 18, 50 10, 50 8 C 50 10, 57 18, 61 30 C 64 29, 67 26, 66 16 C 73 22, 79 34, 79 48 C 79 44, 82 42, 84 32 C 89 40, 90 54, 84 68 C 86 64, 89 60, 91 50 C 93 64, 88 80, 78 88 C 68 96, 32 96, 22 88 C 12 80, 7 64, 9 50 C 11 60, 14 64, 16 68 Z"
						fill="url(#fireBase)"
						opacity="0.9"
					/>
					{/* Inner bright core flames */}
					<path
						d="M 24 64 C 20 54, 22 45, 26 38 C 28 44, 30 46, 32 46 C 34 36, 40 26, 46 22 C 45 28, 48 30, 50 30 C 52 30, 55 28, 54 22 C 60 26, 66 36, 68 46 C 70 46, 72 44, 74 38 C 78 45, 80 54, 76 64 C 74 61, 76 56, 78 52 C 78 68, 70 82, 50 84 C 30 82, 22 68, 22 52 C 24 56, 26 61, 24 64 Z"
						fill="url(#fireTip)"
						opacity="0.85"
					/>
				</g>
			);

		case 'starry_halo':
			return (
				<g id="dec-halo">
					<defs>
						<linearGradient id="haloGold" x1="0" y1="0" x2="1" y2="0">
							<stop offset="0%" stopColor="#ffe066" />
							<stop offset="50%" stopColor="#fff9db" />
							<stop offset="100%" stopColor="#fcc419" />
						</linearGradient>
					</defs>
					{/* Floating Angel Halo */}
					<g className={styles.animatedHalo}>
						<ellipse
							cx="50"
							cy="15"
							rx="28"
							ry="7"
							stroke="url(#haloGold)"
							strokeWidth="3.2"
							fill="none"
							strokeLinecap="round"
						/>
						<ellipse
							cx="50"
							cy="15"
							rx="26"
							ry="5.5"
							stroke="#fff"
							strokeWidth="1"
							fill="none"
							opacity="0.75"
						/>
					</g>
					{/* 4-point twinkle stars */}
					<g transform="translate(18, 22)" className={styles.animatedStar}>
						<path d="M 0 -4 Q 0 0 4 0 Q 0 0 0 4 Q 0 0 -4 0 Q 0 0 0 -4 Z" fill="#ffd43b" />
					</g>
					<g transform="translate(82, 20)" className={styles.animatedStarAlt}>
						<path d="M 0 -5 Q 0 0 5 0 Q 0 0 0 5 Q 0 0 -5 0 Q 0 0 0 -5 Z" fill="#ffd43b" />
					</g>
					<g transform="translate(12, 46)" className={styles.animatedStarAlt}>
						<path d="M 0 -3 Q 0 0 3 0 Q 0 0 0 3 Q 0 0 -3 0 Q 0 0 0 -3 Z" fill="#fff9db" />
					</g>
					<g transform="translate(88, 48)" className={styles.animatedStar}>
						<path d="M 0 -3.5 Q 0 0 3.5 0 Q 0 0 0 3.5 Q 0 0 -3.5 0 Q 0 0 0 -3.5 Z" fill="#fff9db" />
					</g>
				</g>
			);

		case 'royal_crown':
			return (
				<g id="dec-crown">
					<defs>
						<linearGradient id="crownGold" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="#ffe066" />
							<stop offset="100%" stopColor="#e67700" />
						</linearGradient>
					</defs>
					{/* 5-Peak Imperial Crown */}
					<g transform="translate(0, 2)">
						{/* Crown body */}
						<path
							d="M 26 26 L 22 12 L 36 19 L 50 8 L 64 19 L 78 12 L 74 26 C 66 28, 34 28, 26 26 Z"
							fill="url(#crownGold)"
							stroke="#d9480f"
							strokeWidth="1.2"
							strokeLinejoin="round"
						/>
						{/* Crown Arch Band */}
						<path
							d="M 26 26 C 34 29, 66 29, 74 26 L 75 29 C 66 32, 34 32, 25 29 Z"
							fill="#d9480f"
						/>
						{/* Crown Peak Pearls */}
						<circle cx="22" cy="11" r="2.2" fill="#fff" stroke="#fcc419" strokeWidth="0.8" />
						<circle cx="36" cy="18" r="2" fill="#fff" stroke="#fcc419" strokeWidth="0.8" />
						<circle cx="50" cy="7" r="2.8" fill="#fff" stroke="#fcc419" strokeWidth="0.8" />
						<circle cx="64" cy="18" r="2" fill="#fff" stroke="#fcc419" strokeWidth="0.8" />
						<circle cx="78" cy="11" r="2.2" fill="#fff" stroke="#fcc419" strokeWidth="0.8" />
						{/* Jewels embedded on band */}
						<circle cx="34" cy="24" r="1.8" fill="#1864ab" />
						<circle cx="50" cy="24" r="2.2" fill="#c92a2a" />
						<circle cx="66" cy="24" r="1.8" fill="#2b8a3e" />
					</g>
				</g>
			);

		case 'pixel_quest':
			return (
				<g id="dec-pixel">
					{/* 8-bit stepped brackets */}
					{/* Top Left */}
					<rect x="10" y="10" width="12" height="4" fill="#51cf66" />
					<rect x="10" y="14" width="4" height="12" fill="#51cf66" />
					<rect x="14" y="14" width="4" height="4" fill="#2f9e44" />
					{/* Top Right */}
					<rect x="78" y="10" width="12" height="4" fill="#51cf66" />
					<rect x="86" y="14" width="4" height="12" fill="#51cf66" />
					<rect x="82" y="14" width="4" height="4" fill="#2f9e44" />
					{/* Bottom Left */}
					<rect x="10" y="86" width="12" height="4" fill="#22b8cf" />
					<rect x="10" y="74" width="4" height="12" fill="#22b8cf" />
					<rect x="14" y="82" width="4" height="4" fill="#1098ad" />
					{/* Bottom Right */}
					<rect x="78" y="86" width="12" height="4" fill="#22b8cf" />
					<rect x="86" y="74" width="4" height="12" fill="#22b8cf" />
					<rect x="82" y="82" width="4" height="4" fill="#1098ad" />
					{/* Pixel Heart at bottom center */}
					<g transform="translate(44, 84)">
						<rect x="2" y="0" width="3" height="3" fill="#ff4d4f" />
						<rect x="7" y="0" width="3" height="3" fill="#ff4d4f" />
						<rect x="0" y="3" width="12" height="3" fill="#ff4d4f" />
						<rect x="2" y="6" width="8" height="3" fill="#ff4d4f" />
						<rect x="4" y="9" width="4" height="3" fill="#ff4d4f" />
					</g>
				</g>
			);

		case 'spectral_fog':
			return (
				<g id="dec-spectral" className={styles.animatedSpectral}>
					<defs>
						<linearGradient id="spectralGrad" x1="0" y1="1" x2="0" y2="0">
							<stop offset="0%" stopColor="#7048e8" />
							<stop offset="60%" stopColor="#b197fc" />
							<stop offset="100%" stopColor="#74c0fc" />
						</linearGradient>
					</defs>
					<path
						d="M 14 74 C 8 60, 10 42, 16 34 C 18 42, 22 46, 22 52 C 22 36, 30 24, 38 18 C 36 28, 40 32, 44 32 C 48 18, 56 12, 54 8 C 58 14, 64 22, 66 32 C 70 30, 72 26, 70 18 C 78 24, 84 36, 84 52 C 84 46, 88 42, 90 34 C 94 42, 96 60, 90 74 C 84 86, 72 92, 50 92 C 28 92, 18 86, 14 74 Z"
						fill="url(#spectralGrad)"
						opacity="0.75"
					/>
					<circle cx="20" cy="30" r="2.5" fill="#e5dbff" opacity="0.8" />
					<circle cx="80" cy="28" r="2.5" fill="#e5dbff" opacity="0.8" />
					<circle cx="50" cy="10" r="2" fill="#d0ebff" opacity="0.9" />
				</g>
			);

		case 'autumn_leaves':
			return (
				<g id="dec-autumn">
					<defs>
						<linearGradient id="autumnLeaf1" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#ff922b" />
							<stop offset="100%" stopColor="#c92a2a" />
						</linearGradient>
						<linearGradient id="autumnLeaf2" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#ffd43b" />
							<stop offset="100%" stopColor="#f76707" />
						</linearGradient>
					</defs>
					{/* Swirling Wind Line */}
					<path
						d="M 12 60 C 8 40, 22 18, 44 14 C 70 10, 92 26, 88 52 C 86 68, 72 88, 50 88"
						stroke="#ffa94d"
						strokeWidth="1.2"
						strokeDasharray="6 4"
						fill="none"
					/>
					{/* Leaves */}
					<g transform="translate(24, 18) rotate(-20)" className={styles.animatedPetal}>
						<path d="M 0 -7 C 5 -4, 5 4, 0 7 C -5 4, -5 -4, 0 -7 Z" fill="url(#autumnLeaf1)" />
						<line x1="0" y1="-5" x2="0" y2="6" stroke="#fff" strokeWidth="0.6" opacity="0.6" />
					</g>
					<g transform="translate(74, 18) rotate(30)" className={styles.animatedPetal}>
						<path d="M 0 -8 C 6 -5, 6 5, 0 8 C -6 5, -6 -5, 0 -8 Z" fill="url(#autumnLeaf2)" />
						<line x1="0" y1="-6" x2="0" y2="7" stroke="#fff" strokeWidth="0.6" opacity="0.6" />
					</g>
					<g transform="translate(88, 56) rotate(60)" className={styles.animatedPetal}>
						<path d="M 0 -6 C 4 -3, 4 3, 0 6 C -4 3, -4 -3, 0 -6 Z" fill="url(#autumnLeaf1)" />
					</g>
					<g transform="translate(14, 52) rotate(-45)" className={styles.animatedPetal}>
						<path d="M 0 -6 C 4 -3, 4 3, 0 6 C -4 3, -4 -3, 0 -6 Z" fill="url(#autumnLeaf2)" />
					</g>
				</g>
			);

		case 'bubble_tea':
			return (
				<g id="dec-boba">
					<defs>
						<linearGradient id="bobaStraw" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#fcc419" />
							<stop offset="100%" stopColor="#e599f7" />
						</linearGradient>
					</defs>
					{/* Diagonal Straw Top Right */}
					<rect
						x="72"
						y="6"
						width="5.5"
						height="28"
						rx="2.5"
						transform="rotate(30 72 6)"
						fill="url(#bobaStraw)"
						stroke="#be4bdb"
						strokeWidth="0.8"
					/>
					{/* Boba pearls lined around bottom */}
					<g transform="translate(26, 82)">
						<circle cx="0" cy="0" r="4.2" fill="#212529" stroke="#495057" strokeWidth="0.8" />
						<circle cx="-1.2" cy="-1.2" r="1.2" fill="#868e96" />
					</g>
					<g transform="translate(38, 86)">
						<circle cx="0" cy="0" r="4.5" fill="#212529" stroke="#495057" strokeWidth="0.8" />
						<circle cx="-1.2" cy="-1.2" r="1.3" fill="#868e96" />
					</g>
					<g transform="translate(50, 88)">
						<circle cx="0" cy="0" r="4.8" fill="#212529" stroke="#495057" strokeWidth="0.8" />
						<circle cx="-1.4" cy="-1.4" r="1.4" fill="#868e96" />
					</g>
					<g transform="translate(62, 86)">
						<circle cx="0" cy="0" r="4.5" fill="#212529" stroke="#495057" strokeWidth="0.8" />
						<circle cx="-1.2" cy="-1.2" r="1.3" fill="#868e96" />
					</g>
					<g transform="translate(74, 82)">
						<circle cx="0" cy="0" r="4.2" fill="#212529" stroke="#495057" strokeWidth="0.8" />
						<circle cx="-1.2" cy="-1.2" r="1.2" fill="#868e96" />
					</g>
					{/* Cute Sparkles */}
					<circle cx="20" cy="24" r="2" fill="#fcc419" />
					<circle cx="24" cy="20" r="1.2" fill="#fcc419" />
				</g>
			);

		case 'magic_portal':
			return (
				<g id="dec-portal">
					<defs>
						<linearGradient id="portalOuter" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#7950f2" />
							<stop offset="50%" stopColor="#20c997" />
							<stop offset="100%" stopColor="#be4bdb" />
						</linearGradient>
					</defs>
					<g className={styles.animatedPortalOuter}>
						<circle
							cx="50"
							cy="50"
							r="44"
							stroke="url(#portalOuter)"
							strokeWidth="2.5"
							strokeDasharray="20 12 8 12"
							fill="none"
						/>
					</g>
					<g className={styles.animatedPortalInner}>
						<circle
							cx="50"
							cy="50"
							r="46.5"
							stroke="#20c997"
							strokeWidth="1.2"
							strokeDasharray="6 18"
							fill="none"
							opacity="0.8"
						/>
					</g>
					<circle cx="14" cy="50" r="2.5" fill="#20c997" />
					<circle cx="86" cy="50" r="2.5" fill="#20c997" />
					<circle cx="50" cy="14" r="2.5" fill="#7950f2" />
					<circle cx="50" cy="86" r="2.5" fill="#7950f2" />
				</g>
			);

		case 'frozen_frost':
			return (
				<g id="dec-frost">
					<defs>
						<linearGradient id="iceGrad" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="#e7f5ff" />
							<stop offset="100%" stopColor="#74c0fc" />
						</linearGradient>
					</defs>
					{/* Hanging icicles from top */}
					<polygon points="26,16 29,26 32,16" fill="url(#iceGrad)" />
					<polygon points="34,14 38,30 42,14" fill="url(#iceGrad)" />
					<polygon points="46,12 50,32 54,12" fill="url(#iceGrad)" />
					<polygon points="58,14 62,30 66,14" fill="url(#iceGrad)" />
					<polygon points="68,16 71,26 74,16" fill="url(#iceGrad)" />
					{/* Crystal snowflakes */}
					<g transform="translate(18, 50) scale(0.8)">
						<line x1="-6" y1="0" x2="6" y2="0" stroke="#d0ebff" strokeWidth="1.5" strokeLinecap="round" />
						<line x1="0" y1="-6" x2="0" y2="6" stroke="#d0ebff" strokeWidth="1.5" strokeLinecap="round" />
						<line x1="-4" y1="-4" x2="4" y2="4" stroke="#d0ebff" strokeWidth="1.5" strokeLinecap="round" />
						<line x1="-4" y1="4" x2="4" y2="-4" stroke="#d0ebff" strokeWidth="1.5" strokeLinecap="round" />
					</g>
					<g transform="translate(82, 50) scale(0.8)">
						<line x1="-6" y1="0" x2="6" y2="0" stroke="#d0ebff" strokeWidth="1.5" strokeLinecap="round" />
						<line x1="0" y1="-6" x2="0" y2="6" stroke="#d0ebff" strokeWidth="1.5" strokeLinecap="round" />
						<line x1="-4" y1="-4" x2="4" y2="4" stroke="#d0ebff" strokeWidth="1.5" strokeLinecap="round" />
						<line x1="-4" y1="4" x2="4" y2="-4" stroke="#d0ebff" strokeWidth="1.5" strokeLinecap="round" />
					</g>
				</g>
			);

		case 'demon_horns':
			return (
				<g id="dec-demon-horns">
					<defs>
						<linearGradient id="demonHornGrad" x1="0" y1="1" x2="0" y2="0">
							<stop offset="0%" stopColor="#450a0a" />
							<stop offset="50%" stopColor="#dc2626" />
							<stop offset="100%" stopColor="#ff4d4d" />
						</linearGradient>
					</defs>
					{/* Left Demonic Horn */}
					<path
						d="M 28 32 C 16 26, 12 12, 16 4 C 22 14, 25 18, 33 26 Z"
						fill="url(#demonHornGrad)"
						stroke="#991b1b"
						strokeWidth="1.2"
						strokeLinejoin="round"
					/>
					<path d="M 22 24 C 18 18, 16 11, 18 7" stroke="#fca5a5" strokeWidth="0.8" fill="none" opacity="0.7" />
					{/* Right Demonic Horn */}
					<path
						d="M 72 32 C 84 26, 88 12, 84 4 C 78 14, 75 18, 67 26 Z"
						fill="url(#demonHornGrad)"
						stroke="#991b1b"
						strokeWidth="1.2"
						strokeLinejoin="round"
					/>
					<path d="M 78 24 C 82 18, 84 11, 82 7" stroke="#fca5a5" strokeWidth="0.8" fill="none" opacity="0.7" />
					{/* Rising Fire Sparks / Embers */}
					<g className={styles.animatedDemonEmber}>
						<circle cx="20" cy="42" r="1.6" fill="#ef4444" />
						<circle cx="80" cy="40" r="1.5" fill="#f87171" />
						<circle cx="16" cy="58" r="1.8" fill="#fca5a5" />
						<circle cx="84" cy="56" r="1.8" fill="#ef4444" />
						<circle cx="28" cy="78" r="1.5" fill="#ef4444" />
						<circle cx="72" cy="80" r="1.5" fill="#f87171" />
					</g>
				</g>
			);

		case 'cosmic_galaxy':
			return (
				<g id="dec-cosmic-galaxy">
					<defs>
						<linearGradient id="galaxyGrad" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#c084fc" />
							<stop offset="50%" stopColor="#818cf8" />
							<stop offset="100%" stopColor="#22d3ee" />
						</linearGradient>
					</defs>
					<g className={styles.animatedGalaxy}>
						<ellipse
							cx="50"
							cy="50"
							rx="45"
							ry="18"
							stroke="url(#galaxyGrad)"
							strokeWidth="2.2"
							fill="none"
						/>
						<ellipse
							cx="50"
							cy="50"
							rx="47"
							ry="19.5"
							stroke="#e0e7ff"
							strokeWidth="0.8"
							strokeDasharray="4 10"
							fill="none"
							opacity="0.85"
						/>
					</g>
					{/* Starbursts */}
					<g transform="translate(14, 38)" className={styles.animatedStar}>
						<path d="M 0 -4 Q 0 0 4 0 Q 0 0 0 4 Q 0 0 -4 0 Q 0 0 0 -4 Z" fill="#38bdf8" />
					</g>
					<g transform="translate(86, 62)" className={styles.animatedStarAlt}>
						<path d="M 0 -4.5 Q 0 0 4.5 0 Q 0 0 0 4.5 Q 0 0 -4.5 0 Q 0 0 0 -4.5 Z" fill="#c084fc" />
					</g>
					<circle cx="30" cy="18" r="1.5" fill="#ffffff" opacity="0.9" />
					<circle cx="70" cy="82" r="1.5" fill="#ffffff" opacity="0.9" />
				</g>
			);

		case 'lightning_arc':
			return (
				<g id="dec-lightning-arc" className={styles.animatedLightning}>
					<defs>
						<linearGradient id="boltGrad" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="#38bdf8" />
							<stop offset="100%" stopColor="#818cf8" />
						</linearGradient>
					</defs>
					{/* Left Crackling Bolt */}
					<polyline
						points="14,28 8,42 16,46 10,64 18,60 14,76"
						stroke="url(#boltGrad)"
						strokeWidth="2.2"
						strokeLinecap="round"
						strokeLinejoin="round"
						fill="none"
					/>
					{/* Right Crackling Bolt */}
					<polyline
						points="86,28 92,42 84,46 90,64 82,60 86,76"
						stroke="url(#boltGrad)"
						strokeWidth="2.2"
						strokeLinecap="round"
						strokeLinejoin="round"
						fill="none"
					/>
					{/* Top Bolt */}
					<polyline
						points="36,12 46,6 44,14 56,8 52,15 62,10"
						stroke="#67e8f9"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						fill="none"
					/>
					{/* Bottom Accent */}
					<line x1="42" y1="91" x2="58" y2="91" stroke="#38bdf8" strokeWidth="2.2" strokeLinecap="round" />
				</g>
			);

		case 'laurel_wreath':
			return (
				<g id="dec-laurel-wreath">
					<defs>
						<linearGradient id="laurelGold" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="#fde047" />
							<stop offset="100%" stopColor="#ca8a04" />
						</linearGradient>
					</defs>
					{/* Left Arch Branch */}
					<path
						d="M 50 90 C 24 90, 14 70, 14 50 C 14 30, 26 16, 44 12"
						stroke="#a16207"
						strokeWidth="1.6"
						strokeLinecap="round"
						fill="none"
					/>
					{/* Right Arch Branch */}
					<path
						d="M 50 90 C 76 90, 86 70, 86 50 C 86 30, 74 16, 56 12"
						stroke="#a16207"
						strokeWidth="1.6"
						strokeLinecap="round"
						fill="none"
					/>
					{/* Paired Leaves - Left */}
					<ellipse cx="20" cy="30" rx="3.5" ry="7" transform="rotate(-35 20 30)" fill="url(#laurelGold)" stroke="#854d0e" strokeWidth="0.8" />
					<ellipse cx="14" cy="48" rx="3.5" ry="7" transform="rotate(-15 14 48)" fill="url(#laurelGold)" stroke="#854d0e" strokeWidth="0.8" />
					<ellipse cx="18" cy="66" rx="3.5" ry="7" transform="rotate(20 18 66)" fill="url(#laurelGold)" stroke="#854d0e" strokeWidth="0.8" />
					<ellipse cx="32" cy="82" rx="3.5" ry="7" transform="rotate(50 32 82)" fill="url(#laurelGold)" stroke="#854d0e" strokeWidth="0.8" />
					{/* Paired Leaves - Right */}
					<ellipse cx="80" cy="30" rx="3.5" ry="7" transform="rotate(35 80 30)" fill="url(#laurelGold)" stroke="#854d0e" strokeWidth="0.8" />
					<ellipse cx="86" cy="48" rx="3.5" ry="7" transform="rotate(15 86 48)" fill="url(#laurelGold)" stroke="#854d0e" strokeWidth="0.8" />
					<ellipse cx="82" cy="66" rx="3.5" ry="7" transform="rotate(-20 82 66)" fill="url(#laurelGold)" stroke="#854d0e" strokeWidth="0.8" />
					<ellipse cx="68" cy="82" rx="3.5" ry="7" transform="rotate(-50 68 82)" fill="url(#laurelGold)" stroke="#854d0e" strokeWidth="0.8" />
					{/* Emerald Gems */}
					<circle cx="50" cy="9" r="3" fill="#10b981" stroke="#047857" strokeWidth="1" />
					<circle cx="50" cy="90" r="3.2" fill="#e11d48" stroke="#9f1239" strokeWidth="1" />
				</g>
			);

		case 'vampire_bat':
			return (
				<g id="dec-vampire-bat">
					<defs>
						<linearGradient id="moonGrad" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#c084fc" />
							<stop offset="100%" stopColor="#4c1d95" />
						</linearGradient>
					</defs>
					{/* Gothic Crescent Moon */}
					<path
						d="M 86 14 C 74 14, 66 24, 66 36 C 66 48, 76 56, 88 56 C 82 50, 78 42, 78 34 C 78 26, 82 18, 86 14 Z"
						fill="url(#moonGrad)"
					/>
					{/* Bat 1 Top Left */}
					<g transform="translate(24, 20)" className={styles.animatedBat}>
						<path
							d="M 0 0 Q -5 -6 -10 -2 Q -6 3 0 1 Q 6 3 10 -2 Q 5 -6 0 0 Z"
							fill="#a855f7"
						/>
						<circle cx="-1.5" cy="-0.5" r="0.6" fill="#ff4d4d" />
						<circle cx="1.5" cy="-0.5" r="0.6" fill="#ff4d4d" />
					</g>
					{/* Bat 2 Mid Left */}
					<g transform="translate(14, 54) scale(0.85)" className={styles.animatedBat}>
						<path
							d="M 0 0 Q -5 -6 -10 -2 Q -6 3 0 1 Q 6 3 10 -2 Q 5 -6 0 0 Z"
							fill="#7c3aed"
						/>
					</g>
					{/* Bat 3 Bottom Right */}
					<g transform="translate(80, 76) scale(0.75)" className={styles.animatedBat}>
						<path
							d="M 0 0 Q -5 -6 -10 -2 Q -6 3 0 1 Q 6 3 10 -2 Q 5 -6 0 0 Z"
							fill="#9333ea"
						/>
					</g>
				</g>
			);

		case 'diamond_glow':
			return (
				<g id="dec-diamond-glow">
					<defs>
						<linearGradient id="diamondGrad" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#ffffff" />
							<stop offset="50%" stopColor="#38bdf8" />
							<stop offset="100%" stopColor="#c084fc" />
						</linearGradient>
					</defs>
					{/* Apex Cut Diamond */}
					<g transform="translate(50, 11)" className={styles.animatedDiamond}>
						<polygon points="0,-7 6,-3 4,5 -4,5 -6,-3" fill="url(#diamondGrad)" stroke="#0284c7" strokeWidth="0.8" />
						<polyline points="-6,-3 6,-3" stroke="#ffffff" strokeWidth="0.7" opacity="0.8" />
						<polygon points="0,-7 3,-3 0,5 -3,-3" fill="#e0f2fe" opacity="0.6" />
					</g>
					{/* Left Diamond */}
					<g transform="translate(12, 50) scale(0.8)" className={styles.animatedDiamond}>
						<polygon points="0,-6 5,-2 3,4 -3,4 -5,-2" fill="url(#diamondGrad)" stroke="#0284c7" strokeWidth="0.8" />
					</g>
					{/* Right Diamond */}
					<g transform="translate(88, 50) scale(0.8)" className={styles.animatedDiamond}>
						<polygon points="0,-6 5,-2 3,4 -3,4 -5,-2" fill="url(#diamondGrad)" stroke="#0284c7" strokeWidth="0.8" />
					</g>
					{/* Bottom Left Small Diamond */}
					<g transform="translate(26, 82) scale(0.65)" className={styles.animatedDiamond}>
						<polygon points="0,-6 5,-2 3,4 -3,4 -5,-2" fill="url(#diamondGrad)" />
					</g>
					{/* Bottom Right Small Diamond */}
					<g transform="translate(74, 82) scale(0.65)" className={styles.animatedDiamond}>
						<polygon points="0,-6 5,-2 3,4 -3,4 -5,-2" fill="url(#diamondGrad)" />
					</g>
				</g>
			);

		case 'kawaii_frog':
			return (
				<g id="dec-kawaii-frog">
					<defs>
						<linearGradient id="frogGreen" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="#86efac" />
							<stop offset="100%" stopColor="#22c55e" />
						</linearGradient>
					</defs>
					{/* Frog Dome Cap Top */}
					<path
						d="M 22 28 C 28 14, 72 14, 78 28 Z"
						fill="url(#frogGreen)"
						stroke="#15803d"
						strokeWidth="1.2"
					/>
					{/* Left Frog Eye */}
					<circle cx="32" cy="14" r="7.5" fill="url(#frogGreen)" stroke="#15803d" strokeWidth="1.2" />
					<circle cx="32" cy="14" r="5" fill="#ffffff" />
					<circle cx="33" cy="14" r="3.2" fill="#0f172a" />
					<circle cx="34" cy="13" r="1.2" fill="#ffffff" />
					{/* Right Frog Eye */}
					<circle cx="68" cy="14" r="7.5" fill="url(#frogGreen)" stroke="#15803d" strokeWidth="1.2" />
					<circle cx="68" cy="14" r="5" fill="#ffffff" />
					<circle cx="67" cy="14" r="3.2" fill="#0f172a" />
					<circle cx="68" cy="13" r="1.2" fill="#ffffff" />
					{/* Rosy Cheek Blush */}
					<ellipse cx="22" cy="40" rx="3.5" ry="2" fill="#f43f5e" opacity="0.6" />
					<ellipse cx="78" cy="40" rx="3.5" ry="2" fill="#f43f5e" opacity="0.6" />
				</g>
			);

		case 'heart_radiance':
			return (
				<g id="dec-heart-radiance" className={styles.animatedHeart}>
					<defs>
						<linearGradient id="heartGrad" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#f43f5e" />
							<stop offset="100%" stopColor="#fb7185" />
						</linearGradient>
					</defs>
					{/* Apex Heart */}
					<g transform="translate(50, 11) scale(0.9)">
						<path
							d="M 0 3 C -4 -2, -8 -1, -8 3 C -8 7, 0 11, 0 13 C 0 11, 8 7, 8 3 C 8 -1, 4 -2, 0 3 Z"
							fill="url(#heartGrad)"
						/>
					</g>
					{/* Left Upper Heart */}
					<g transform="translate(16, 34) scale(0.7) rotate(-20)">
						<path
							d="M 0 3 C -4 -2, -8 -1, -8 3 C -8 7, 0 11, 0 13 C 0 11, 8 7, 8 3 C 8 -1, 4 -2, 0 3 Z"
							fill="#fda4af"
						/>
					</g>
					{/* Right Upper Heart */}
					<g transform="translate(84, 34) scale(0.7) rotate(20)">
						<path
							d="M 0 3 C -4 -2, -8 -1, -8 3 C -8 7, 0 11, 0 13 C 0 11, 8 7, 8 3 C 8 -1, 4 -2, 0 3 Z"
							fill="#fda4af"
						/>
					</g>
					{/* Left Lower Heart */}
					<g transform="translate(18, 70) scale(0.75) rotate(-35)">
						<path
							d="M 0 3 C -4 -2, -8 -1, -8 3 C -8 7, 0 11, 0 13 C 0 11, 8 7, 8 3 C 8 -1, 4 -2, 0 3 Z"
							fill="url(#heartGrad)"
						/>
					</g>
					{/* Right Lower Heart */}
					<g transform="translate(82, 70) scale(0.75) rotate(35)">
						<path
							d="M 0 3 C -4 -2, -8 -1, -8 3 C -8 7, 0 11, 0 13 C 0 11, 8 7, 8 3 C 8 -1, 4 -2, 0 3 Z"
							fill="url(#heartGrad)"
						/>
					</g>
					{/* Bottom Center Heart */}
					<g transform="translate(50, 89) scale(0.8)">
						<path
							d="M 0 3 C -4 -2, -8 -1, -8 3 C -8 7, 0 11, 0 13 C 0 11, 8 7, 8 3 C 8 -1, 4 -2, 0 3 Z"
							fill="#f43f5e"
						/>
					</g>
				</g>
			);

		case 'gamer_headset':
			return (
				<g id="dec-gamer-headset">
					<defs>
						<linearGradient id="rgbGrad" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#06b6d4" />
							<stop offset="50%" stopColor="#a855f7" />
							<stop offset="100%" stopColor="#ec4899" />
						</linearGradient>
					</defs>
					{/* Headset Arch */}
					<path
						d="M 16 46 C 14 12, 86 12, 84 46"
						stroke="#1e293b"
						strokeWidth="5.5"
						strokeLinecap="round"
						fill="none"
					/>
					{/* Headset Top Cushion */}
					<path
						d="M 28 20 C 38 15, 62 15, 72 20"
						stroke="#334155"
						strokeWidth="3.5"
						strokeLinecap="round"
						fill="none"
					/>
					{/* Left Earcup with Chroma RGB Glow */}
					<g className={styles.animatedRgb}>
						<circle cx="13" cy="48" r="8" fill="#0f172a" stroke="url(#rgbGrad)" strokeWidth="2.4" />
						<circle cx="13" cy="48" r="4" fill="#334155" />
					</g>
					{/* Right Earcup with Chroma RGB Glow */}
					<g className={styles.animatedRgb}>
						<circle cx="87" cy="48" r="8" fill="#0f172a" stroke="url(#rgbGrad)" strokeWidth="2.4" />
						<circle cx="87" cy="48" r="4" fill="#334155" />
					</g>
					{/* Microphone Boom */}
					<path
						d="M 13 54 Q 18 72 30 73"
						stroke="#64748b"
						strokeWidth="2.2"
						strokeLinecap="round"
						fill="none"
					/>
					<rect x="29" y="71" width="5.5" height="4" rx="2" fill="#0f172a" stroke="#06b6d4" strokeWidth="0.8" />
				</g>
			);

		case 'golden_dragon':
			return (
				<g id="dec-golden-dragon" className={styles.animatedDragon}>
					<defs>
						<linearGradient id="dragonGold" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#fef08a" />
							<stop offset="50%" stopColor="#f59e0b" />
							<stop offset="100%" stopColor="#b45309" />
						</linearGradient>
					</defs>
					{/* Coiling Serpentine Body */}
					<path
						d="M 18 74 C 8 46, 24 16, 50 14 C 76 12, 92 34, 88 66 C 85 86, 68 94, 50 92 C 32 90, 24 82, 18 74 Z"
						stroke="url(#dragonGold)"
						strokeWidth="3.8"
						strokeDasharray="12 3 6 3"
						fill="none"
					/>
					{/* Dragon Head */}
					<g transform="translate(74, 22)">
						<polygon points="0,0 12,-4 16,4 4,6" fill="url(#dragonGold)" stroke="#78350f" strokeWidth="0.8" />
						{/* Horn */}
						<line x1="2" y1="-2" x2="-4" y2="-8" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" />
						{/* Ruby Eye */}
						<circle cx="6" cy="1" r="1.4" fill="#ef4444" />
					</g>
					{/* Dragon Flame Breath */}
					<circle cx="90" cy="22" r="2" fill="#ef4444" />
					<circle cx="94" cy="20" r="1.3" fill="#fbbf24" />
					{/* Dragon Tail Fins */}
					<polygon points="18,74 10,80 14,84 20,78" fill="url(#dragonGold)" />
				</g>
			);

		case 'ocean_waves':
			return (
				<g id="dec-ocean-waves" className={styles.animatedWave}>
					<defs>
						<linearGradient id="waveGrad" x1="0" y1="1" x2="0" y2="0">
							<stop offset="0%" stopColor="#0369a1" />
							<stop offset="60%" stopColor="#0ea5e9" />
							<stop offset="100%" stopColor="#67e8f9" />
						</linearGradient>
					</defs>
					{/* Bottom Rolling Crest Waves */}
					<path
						d="M 12 70 C 14 62, 22 62, 26 68 C 30 74, 38 72, 42 66 C 46 60, 54 60, 58 66 C 62 72, 70 74, 74 68 C 78 62, 86 62, 88 70 C 86 86, 72 96, 50 96 C 28 96, 14 86, 12 70 Z"
						fill="url(#waveGrad)"
					/>
					{/* Wave Foam Tops */}
					<circle cx="26" cy="65" r="2.2" fill="#f0f9ff" />
					<circle cx="42" cy="63" r="2.2" fill="#f0f9ff" />
					<circle cx="58" cy="63" r="2.2" fill="#f0f9ff" />
					<circle cx="74" cy="65" r="2.2" fill="#f0f9ff" />
					{/* Sea Spray Bubbles */}
					<circle cx="34" cy="56" r="1.2" fill="#e0f2fe" />
					<circle cx="66" cy="56" r="1.2" fill="#e0f2fe" />
					<circle cx="50" cy="54" r="1" fill="#e0f2fe" />
				</g>
			);

		case 'poison_skull':
			return (
				<g id="dec-poison-skull" className={styles.animatedToxic}>
					<defs>
						<linearGradient id="toxicGrad" x1="0" y1="0" x2="1" y2="1">
							<stop offset="0%" stopColor="#22c55e" />
							<stop offset="100%" stopColor="#a3e635" />
						</linearGradient>
					</defs>
					{/* Thorny Poison Vine Perimeter */}
					<path
						d="M 14 50 C 14 28, 28 14, 50 14 C 72 14, 86 28, 86 50 C 86 72, 72 86, 50 86 C 28 86, 14 72, 14 50 Z"
						stroke="#15803d"
						strokeWidth="2"
						strokeDasharray="14 6 4 6"
						fill="none"
					/>
					{/* Toxic Bulb Spores */}
					<circle cx="22" cy="24" r="3.2" fill="url(#toxicGrad)" stroke="#166534" strokeWidth="0.8" />
					<circle cx="78" cy="24" r="3.2" fill="url(#toxicGrad)" stroke="#166534" strokeWidth="0.8" />
					<circle cx="12" cy="54" r="3.5" fill="url(#toxicGrad)" stroke="#166534" strokeWidth="0.8" />
					<circle cx="88" cy="54" r="3.5" fill="url(#toxicGrad)" stroke="#166534" strokeWidth="0.8" />
					<circle cx="50" cy="88" r="3.8" fill="url(#toxicGrad)" stroke="#166534" strokeWidth="0.8" />
					{/* Floating Spore Motes */}
					<circle cx="28" cy="16" r="1.3" fill="#bef264" />
					<circle cx="72" cy="16" r="1.3" fill="#bef264" />
					<circle cx="18" cy="66" r="1.4" fill="#bef264" />
					<circle cx="82" cy="66" r="1.4" fill="#bef264" />
				</g>
			);

		default:
			return null;
	}
}
